import React, { useState, useEffect } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { KTIcon } from '../../../../../../_metronic/helpers';
import { toast } from 'react-toastify';
import { getKYCDocument, updateKYCStatus } from '../../../../../services/ClientService';

interface KYCDocumentModalProps {
  show: boolean;
  onHide: () => void;
  leadId: number | null;
  onStatusUpdate: () => void;
}

interface KYCDocumentData {
  id: number;
  status: string;
  kyc_doc_type: string;
  kyc_doc_file: string;
  kyc_doc_file_url: string;
}

const KYCDocumentModal: React.FC<KYCDocumentModalProps> = ({
  show,
  onHide,
  leadId,
  onStatusUpdate,
}) => {
  const [loading, setLoading] = useState(false);
  const [kycData, setKycData] = useState<KYCDocumentData | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  /**
   * Fetches KYC document data for the specified lead ID
   */
  const fetchKYCDocument = async () => {
    if (!leadId) return;
    
    setLoading(true);
    try {
      const response = await getKYCDocument(leadId);
      console.log('KYC API Response:', response.data); // Debug log
      
      if (response.data.success) {
        // Handle nested data structure: response.data.data.data
        const kycData = response.data.data?.data || response.data.data;
        setKycData(kycData);
      } else {
        toast.error(response.data.reason || 'Failed to fetch KYC document');
      }
    } catch (error) {
      console.error('Error fetching KYC document:', error);
      toast.error('Failed to fetch KYC document');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Updates KYC status (approve/reject)
   * @param status - The new status ('approved' or 'rejected')
   */
  const handleStatusUpdate = async (status: 'approved' | 'rejected') => {
    if (!leadId) return;
    
    setActionLoading(true);
    try {
      const response = await updateKYCStatus(leadId, { status });
      if (response.data.success) {
        toast.success(`KYC ${status} successfully`);
        onStatusUpdate(); // Refresh the parent component
        onHide(); // Close the modal
      } else {
        toast.error(response.data.reason || `Failed to ${status} KYC`);
      }
    } catch (error) {
      console.error(`Error ${status}ing KYC:`, error);
      toast.error(`Failed to ${status} KYC`);
    } finally {
      setActionLoading(false);
    }
  };

  /**
   * Downloads the KYC document
   */
  const handleDownload = () => {
    if (kycData?.kyc_doc_file_url) {
      const link = document.createElement('a');
      link.href = kycData.kyc_doc_file_url;
      link.download = `kyc_document_${leadId}.${kycData.kyc_doc_type}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  useEffect(() => {
    if (show && leadId) {
      fetchKYCDocument();
    }
  }, [show, leadId]);

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      backdrop="static"
      keyboard={false}
    >
      <div className="modal-content">
        <div className="modal-header">
          <h2 className="modal-title">KYC Document Review</h2>
          <button
            type="button"
            className="btn btn-icon btn-sm btn-active-light-primary ms-2"
            onClick={onHide}
          >
            <KTIcon iconName="cross" className="svg-icon svg-icon-2x" />
          </button>
        </div>
        
        <div className="modal-body">
          {loading ? (
            <div className="d-flex justify-content-center align-items-center py-5">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
              <span className="ms-3">Loading KYC document...</span>
            </div>
          ) : kycData ? (
            <div className="row">
              {/* Document Type */}
              <div className="col-12 mb-4">
                <label className="form-label fw-bold">Document Type:</label>
                <div className="form-control-plaintext">
                  <span className="badge badge-light-primary fs-6">
                    {kycData.kyc_doc_type?.toUpperCase() || 'N/A'}
                  </span>
                </div>
              </div>

              {/* Document Image */}
              <div className="col-12 mb-4">
                <label className="form-label fw-bold">Document Image:</label>
                <div className="text-center">
                  {kycData.kyc_doc_file_url ? (
                    <div>
                      <img
                        src={kycData.kyc_doc_file_url}
                        alt="KYC Document"
                        className="img-fluid rounded border"
                        style={{ maxHeight: '400px', maxWidth: '100%' }}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          const errorDiv = document.createElement('div');
                          errorDiv.className = 'alert alert-warning';
                          errorDiv.textContent = 'Failed to load document image';
                          target.parentNode?.appendChild(errorDiv);
                        }}
                      />
                      <div className="mt-3">
                        <Button
                          variant="outline-primary"
                          onClick={handleDownload}
                          className="me-2"
                        >
                          <KTIcon iconName="download" className="svg-icon-2" />
                          Download Document
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="alert alert-warning">
                      No document image available
                    </div>
                  )}
                </div>
              </div>

              {/* Current Status */}
              {kycData.status && (
                <div className="col-12 mb-4">
                  <label className="form-label fw-bold">Current Status:</label>
                  <div className="form-control-plaintext">
                    <span className={`badge badge-light-${kycData.status === 'approved' ? 'success' : kycData.status === 'rejected' ? 'danger' : 'warning'} fs-6`}>
                      {kycData.status.toUpperCase()}
                    </span>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="alert alert-danger">
              Failed to load KYC document data
            </div>
          )}
        </div>

        <div className="modal-footer">
          <Button variant="secondary" onClick={onHide}>
            Close
          </Button>
          {kycData && (
            <>
              <Button
                variant="success"
                onClick={() => handleStatusUpdate('approved')}
                disabled={actionLoading}
                className="me-2"
              >
                {actionLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Processing...
                  </>
                ) : (
                  <>
                    <KTIcon iconName="check" className="svg-icon-2" />
                    Approve
                  </>
                )}
              </Button>
              <Button
                variant="danger"
                onClick={() => handleStatusUpdate('rejected')}
                disabled={actionLoading}
              >
                {actionLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Processing...
                  </>
                ) : (
                  <>
                    <KTIcon iconName="cross" className="svg-icon-2" />
                    Reject
                  </>
                )}
              </Button>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default KYCDocumentModal;
